import { clock } from '../core/report';
import { Replayer, type RunFile } from '../core/replay';
import { TICK_RATE } from '../core/types';
import { Renderer, type ViewState } from '../render/renderer';
import { clear, h, setText } from './dom';
import { describe } from './eventText';

/** Watch a recorded run. Seeking re-simulates from the start, which is fast. */
export class ReplayView {
  private run: RunFile;
  private rep: Replayer;
  private renderer: Renderer;
  private root: HTMLElement;
  private raf = 0;
  private last = performance.now();
  private acc = 0;
  private speed = 4;
  private playing = true;
  private eventCursor = 0;
  private el: Record<string, HTMLElement> = {};
  private view: ViewState;
  private onExit: () => void;
  private finished = false;

  constructor(run: RunFile, canvas: HTMLCanvasElement, root: HTMLElement, reduced: boolean, onExit: () => void) {
    this.run = run;
    this.rep = new Replayer(run);
    this.renderer = new Renderer(canvas);
    this.root = root;
    this.onExit = onExit;
    this.view = {
      tool: null, toolDir: 0, hover: null, place: null, dragPath: null, removeRect: null, copyRect: null, selectedId: null,
      showLabels: true, stamp: null, planMode: false, reducedMotion: reduced, showDebt: false,
    };
    const start = this.rep.world.level.features.find((f) => f.t === 'reveal');
    if (start && start.t === 'reveal') {
      this.renderer.cam.cx = start.x;
      this.renderer.cam.cy = start.y;
      this.renderer.cam.zoom = Math.max(12, Math.min(30, Math.round(Math.min(window.innerWidth, window.innerHeight - 160) / (2 * start.r + 6))));
    }
    this.build();
    window.addEventListener('resize', this.onResize);
    window.addEventListener('keydown', this.onKey);
    this.raf = requestAnimationFrame(this.frame);
  }

  private onResize = () => this.renderer.resize();

  private onKey = (e: KeyboardEvent) => {
    if (e.key === ' ') {
      e.preventDefault();
      this.toggle();
    } else if (e.key === 'Escape') this.exit();
  };

  private build() {
    const e = this.el;
    clear(this.root);
    this.root.hidden = false;
    e.title = h('div', { class: 'level-name', text: `Replay: ${this.run.level.name}` });
    e.sub = h('div', { class: 'goal-text', text: 'Recorded run, re-simulated from its action log.' });
    e.verify = h('div', { class: 'goal-text' });
    e.events = h('div', { class: 'side-body' });
    const card = h('section', { class: 'goal panel' }, e.title, e.sub, e.verify);
    e.playBtn = h('button', { class: 'btn primary', onclick: () => this.toggle(), text: 'Pause' });
    const seg = h('div', { class: 'seg', role: 'group', 'aria-label': 'Replay speed' });
    for (const n of [1, 4, 16]) seg.append(h('button', { 'data-speed': n, onclick: () => this.setSpeed(n), text: `${n}x` }));
    e.seg = seg;
    e.time = h('span', { class: 'chip', text: '0:00' });
    e.fill = h('i', { style: { width: '0%' } });
    e.bar = h('div', { class: 'bar', style: { flex: '1', height: '10px', marginTop: '0', cursor: 'pointer' }, onclick: (ev: MouseEvent) => this.seekFromBar(ev) }, e.fill);
    const controls = h(
      'section',
      { class: 'panel', style: { position: 'fixed', left: '50%', bottom: '12px', transform: 'translateX(-50%)', width: 'min(760px, calc(100vw - 24px))', display: 'flex', gap: '10px', alignItems: 'center', padding: '8px 10px' } },
      e.playBtn,
      seg,
      e.bar,
      e.time,
      h('button', { class: 'btn ghost', onclick: () => this.exit(), text: 'Exit' }),
    );
    const log = h('aside', { class: 'side panel', 'aria-label': 'Replay events' }, h('div', { class: 'tabs' }, h('button', { class: 'on', text: 'What happened' })), e.events);
    this.root.append(h('div', { class: 'topbar' }, card), log, controls);
    this.refreshSpeed();
  }

  private refreshSpeed() {
    for (const b of this.el.seg.querySelectorAll('button')) b.classList.toggle('on', Number(b.dataset.speed) === this.speed);
    this.el.playBtn.textContent = this.playing ? 'Pause' : 'Play';
  }

  private setSpeed(n: number) {
    this.speed = n;
    this.playing = true;
    this.refreshSpeed();
  }

  private toggle() {
    if (this.rep.done) this.seek(0);
    this.playing = !this.playing;
    this.refreshSpeed();
  }

  private seekFromBar(ev: MouseEvent) {
    const r = (ev.currentTarget as HTMLElement).getBoundingClientRect();
    this.seek(Math.round(((ev.clientX - r.left) / r.width) * this.run.ticks));
  }

  /** Jump to a tick by re-simulating from the start. */
  private seek(tick: number) {
    this.rep = new Replayer(this.run);
    while (!this.rep.done && this.rep.world.tick < tick) this.rep.step();
    this.rep.world.fx.length = 0;
    this.eventCursor = 0;
    clear(this.el.events);
    this.finished = false;
    setText(this.el.verify, '');
    this.drainEvents(true);
  }

  private drainEvents(silent = false) {
    const w = this.rep.world;
    const rows: HTMLElement[] = [];
    while (this.eventCursor < w.events.length) {
      const ev = w.events[this.eventCursor++];
      const d = describe(ev, w);
      if (!d) continue;
      rows.push(h('div', { class: `log-row ${d.tone}` }, h('time', { text: clock(ev.t / TICK_RATE) }), h('span', { text: d.text })));
    }
    if (rows.length) {
      for (const r of rows) this.el.events.prepend(r);
      while (this.el.events.children.length > 60) this.el.events.lastElementChild?.remove();
    }
    if (!silent) for (const f of w.fx) this.renderer.addFx(f, this.view.reducedMotion);
    w.fx.length = 0;
  }

  private frame = (now: number) => {
    const dt = Math.min(0.1, (now - this.last) / 1000);
    this.last = now;
    const w = this.rep.world;
    if (this.playing && !this.rep.done) {
      this.acc += dt * this.speed;
      let n = 0;
      while (this.acc >= 1 / TICK_RATE && n < 200 && !this.rep.done) {
        this.rep.step();
        this.acc -= 1 / TICK_RATE;
        n++;
      }
      this.drainEvents();
    }
    if (this.rep.done && !this.finished) {
      this.finished = true;
      this.playing = false;
      this.refreshSpeed();
      const v = this.rep.verify();
      setText(this.el.verify, v.detail);
      (this.el.verify as HTMLElement).style.color = v.ok ? 'var(--good)' : 'var(--warn)';
    }
    this.el.fill.style.width = `${Math.min(100, (w.tick / Math.max(1, this.run.ticks)) * 100)}%`;
    setText(this.el.time, `${clock(w.seconds)} of ${clock(this.run.ticks / TICK_RATE)}`);
    this.renderer.render(w, this.view, 0, dt);
    this.raf = requestAnimationFrame(this.frame);
  };

  exit() {
    this.destroy();
    this.onExit();
  }

  destroy() {
    cancelAnimationFrame(this.raf);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onKey);
    clear(this.root);
    this.root.hidden = true;
  }
}
